/**
 * CB Core Data Protector Module
 * by Lev K. College Board 2016-2018
 */

import utils from './coreutils';
import iam from './cb_iam';

import cookie from 'js-cookie';

  // cookie names
  var CB_COOKIE_NAMES = Object.freeze({
    CB_LOGIN: 'cb_login',
    UL_SESSION: 'ul_session',
    PROTECTOR_HEART_BEAT: 'protector_heart_beat',
    PROTECTOR_CB_LOGIN: 'protector_cb_login'
  });

  var MODES = Object.freeze({
    REGULAR: 'regular',
    AGGRESSIVE: 'aggressive'
  });

  // browser storage keys
  var LOCAL_STORAGE_KEY = "protectorLocal";

  // heartbeat interval in seconds
  var HEART_BEAT_INTERVAL = 2;
  // data protection timeouts in seconds
  var DEFAULT_TAB_REMOVED_TIMEOUT = 10;
  var DEFAULT_INVISIBLE_TIMEOUT = 600;   // 10 mins

  var INVALID_INTERVAL_ID = -1;
  var intervalID = INVALID_INTERVAL_ID;

  // logger
  var logger = utils.Logger.getLogger('cb.core.protector');

  // by default protection is enabled
  var isProtectionEnabled = true;

  // protection mode (by default regular)
  var mode = MODES.REGULAR;

  // tab removed timeout
  var tabRemovedTimeoutInSecs = DEFAULT_TAB_REMOVED_TIMEOUT;

  // invisibility timeout
  var invisibleTimeoutInSecs = DEFAULT_INVISIBLE_TIMEOUT;
  // by default invisible timeout is NOT enforced
  var invisibleTimeoutEnforced = false;
  var invisibleTimerID = INVALID_INTERVAL_ID;
  var pageInfo = {};

  // already initialized flag
  var isInitialized = false;

  // waiting for session check flag
  var isEnforcementPending = false;

  // init
  //---------------------------------------------------------------------------
  function init(paramObject) {

    if (isInitialized) {
      logger.error ("protector has been already initialized: ignoring this attempt");
      return;
    }

    isInitialized = true;

    var iamConfig = utils.DeploymentProfile.iam;
    if ( iamConfig && iamConfig.loginToken && 
         iamConfig.loginToken.type && (iamConfig.loginToken.type==='Bearer')) {
      logger.info("data protection is disabled for partners");
      return;
    }

    // paramObject overrides the options in levity deployment profile
    if (!paramObject && utils.DeploymentProfile &&
        utils.DeploymentProfile.dataProtector) {
      paramObject = utils.DeploymentProfile.dataProtector;
    }

    logger.debug("init(), supplied options: " + JSON.stringify(paramObject));

    var iamBus = iam.getEventBus();
    // subscribe to logout event regardless whether protection is enabled or not
    iamBus.on(iam.events.Logout,onLogout);

    if (paramObject) {
      // quick sanity check
      if (typeof paramObject!=='object')
        throw 'cb.core.protector.init(): requires one parameter which must be an object';

      // handle protectionEnabled prop
      if (paramObject.protectionEnabled!==undefined) {
        if ((paramObject.protectionEnabled===false) ||
            (paramObject.protectionEnabled==='false') ) {
          isProtectionEnabled = false;
        }
        else {
          if (paramObject.protectionEnabled==='join') {
            // check if the protection is currently on
            var lastHB = cookie.get(CB_COOKIE_NAMES.PROTECTOR_HEART_BEAT);
            if (lastHB){
              if ((Date.now()-lastHB) > tabRemovedTimeoutInSecs*1000)
                isProtectionEnabled = false;
            }
            else {
              isProtectionEnabled = false;
            }
            if (isProtectionEnabled)
              logger.info("Joining data protection ON");
            else {
              logger.info("Joining data protection OFF");
            }
          }
        }
      }

      if (!isProtectionEnabled) {
        logger.info("data protection is DISABLED!");
        return;
      }

      var tmp = 0;

      if (paramObject.mode && (paramObject.mode===MODES.AGGRESSIVE)) {
        mode = MODES.AGGRESSIVE;
        logger.info("using new mode=" + mode);
      }

      if (paramObject.tabRemovedTimeoutInSecs) {
        tmp = parseInt(paramObject.tabRemovedTimeoutInSecs, 10);
        if (tmp) {
          tabRemovedTimeoutInSecs = tmp;
        }
      }

      if (paramObject.invisibleTimeoutEnforced) {
        invisibleTimeoutEnforced = (paramObject.invisibleTimeoutEnforced===true);
      }

      if (paramObject.invisibleTimeoutInSecs) {
        if (invisibleTimeoutEnforced) {
          tmp = parseInt(paramObject.invisibleTimeoutInSecs, 10);
          if (tmp) {
            invisibleTimeoutInSecs = tmp;
          }
        }
        else {
          logger.error ('Supplied invisibleTimeoutInSecs is ignored since invisibleTimeoutEnforced=false');
        }
      }
    }

    logger.info('levity protector options in effect: ' + '\nisProtectionEnabled : ' + isProtectionEnabled +
      '\nprotectionMode : ' + mode +
      '\nprotectionTimeoutInSecs : ' + tabRemovedTimeoutInSecs +
      '\ninvisibleTimeoutEnforced : ' + invisibleTimeoutEnforced +
      (invisibleTimeoutEnforced? '\ninvisibleTimeoutInSecs : ' + invisibleTimeoutInSecs : '') + '\n');


    // intial cleanup
    if (!cookie.get(CB_COOKIE_NAMES.CB_LOGIN))
      onLogout();
      
    // subscribe to login event
    iamBus.on(iam.events.Login, onLogin);

    if (invisibleTimeoutEnforced) {
      document.addEventListener("visibilitychange", onVisibilityChange);
      // get the saved page proetector info from the local storage
      loadPageInfoFromBrowserLS();
    }

    // call the cb data protector
    checkProtection();

    window.onbeforeunload = function(e) {
      logger.debug ('onbeforeunload()');
      //disarm();
    };
  }

  // onHeartBeatInterval
  //---------------------------------------------------------------------------
  function onHeartBeatInterval() {
    //logger.debug("onHeartBeatInterval()");

    // update the heartbeat cookie with the current time
    cookie.set(CB_COOKIE_NAMES.PROTECTOR_HEART_BEAT, Date.now(),
        {
          path: "/",
          domain: ".collegeboard.org",
          secure  : true
        });
  }

  // onVisibilityChange
  //---------------------------------------------------------------------------
  function onVisibilityChange(e) {
    logger.debug ('Got visibility change event: new hidden value=' + document.hidden);

    if (invisibleTimeoutEnforced && document.hidden &&
        iam.getAuthSession().isLoggedIn) {
      // cancel the heartbeat interval
      disarm(false);

      // start the invisible timer
      invisibleTimerID = setInterval (onInvisibleTimeout,
        invisibleTimeoutInSecs*1000);
      logger.debug ('started the invisible interval=' + invisibleTimerID);
    }
    else {
      // cancel the timer
      if (invisibleTimerID!==INVALID_INTERVAL_ID) {
        clearInterval(invisibleTimerID);
        logger.debug ('cancelled the invisible interval=' + invisibleTimerID);
        invisibleTimerID=INVALID_INTERVAL_ID;
        resetPageInfo();
      }
    }
  }

  // onInvisibleTimeout
  //---------------------------------------------------------------------------
  function onInvisibleTimeout() {
    logger.debug("onInvisibleTimeout()");

    // check if there are still visible protected tabs or
    // or the protected invisible ones that did not set invisible timeout
    var lastHB = cookie.get(CB_COOKIE_NAMES.PROTECTOR_HEART_BEAT);
    if ((Date.now()-lastHB) > tabRemovedTimeoutInSecs*1000) {
      /* do not need this complexity for now
      pageInfo.forceLogin=true;
      storePageInfoInBrowserLS();
      */
      enforceProtection();
    }
  }

  // onLogin
  //---------------------------------------------------------------------------
  function onLogin() {
    logger.debug("onLogin()");

    if (isEnforcementPending) {
      enforceProtection();
      return;
    }
    else {
      arm();
    }
  }

  // onLogout
  //---------------------------------------------------------------------------
  function onLogout() {
    logger.debug("onLogout()");
    disarm(true);
  }

  // arm
  //---------------------------------------------------------------------------
  function arm() {
    logger.debug("arm()");

    if (iam.getAuthSession().csrId) {
      logger.warn("loginAs mode: protection disabled!");
      isProtectionEnabled = false;
      return;
    }

    cookie.set(CB_COOKIE_NAMES.PROTECTOR_CB_LOGIN,
        cookie.get(CB_COOKIE_NAMES.CB_LOGIN),
        {
          path: "/",
          domain: ".collegeboard.org",
          secure  : true
        });

    // update the heartbeat once
    onHeartBeatInterval();

    // set up the heartbeat interval timer
    if (intervalID===INVALID_INTERVAL_ID) {
      logger.debug("arm(): starting the heartbeat interval timer");
      intervalID = setInterval(onHeartBeatInterval,HEART_BEAT_INTERVAL*1000);
    }

    // reset the page info
    resetPageInfo();

    isProtectionEnabled = true;
  }

  // disarm
  //---------------------------------------------------------------------------
  function disarm(deleteCookie) {
    logger.debug("disarm(), deleteCookie=" + deleteCookie);

    if (deleteCookie) {
      logger.debug("deleting protector_cb_login cookie...");
      cookie.remove(CB_COOKIE_NAMES.PROTECTOR_CB_LOGIN,
        {
          path: "/",
          domain: ".collegeboard.org",
          secure  : true
        });
    }

    // cancel the heartbeat interval timer
    if (intervalID!==INVALID_INTERVAL_ID) {
      clearInterval(intervalID);
      intervalID = INVALID_INTERVAL_ID;
    }

    resetPageInfo();

    isProtectionEnabled = false;
  }

  // checkProtection
  //---------------------------------------------------------------------------
  function checkProtection() {
    logger.debug("checkProtection()");

    if (iam.getAuthSession().csrId) {
      logger.warn("loginAs mode: disabling protection");
      disarm(true);
      return;
    }

    var lastToken = cookie.get(CB_COOKIE_NAMES.PROTECTOR_CB_LOGIN); // could return undefined
    var curToken = cookie.get(CB_COOKIE_NAMES.CB_LOGIN);

    if (!curToken) {
      // user is not logged in
      logger.info('user is not logged in: protection disabled');
      return;
    }

    if (lastToken!=curToken) {
      // login is in process, allow one time pass
      logger.info('lastLoginToken!=curLoginToken: login is in process, allow one time pass');
      arm();
      return;
    }

    if (invisibleTimeoutEnforced && pageInfo.forceLogin) {
      logger.info('invisibleTimeoutEnforced && pageInfo.forceLogin: enforce!');
      enforceProtection();
      return;
    }

    var lastHB = parseInt(cookie.get(CB_COOKIE_NAMES.PROTECTOR_HEART_BEAT),10);

    if (lastHB) {
      var secondsPassed = (Date.now()-lastHB)/1000;
      if ( secondsPassed > tabRemovedTimeoutInSecs) {
        // if we have the info about the session and it's not expired yet
        // enforce right away
        if (iam.getAuthSession().isLoggedIn) {
          logger.info("enforcing protection right away, secondsPassed=" + secondsPassed);
          enforceProtection();
        }
        else {
          // need to give time for a session check
          logger.info("enforcement pending: waiting for the session check to return...");
          isEnforcementPending = true;
        }
      }
      else {
        // need to start the heartbeat after the refresh/reload of the protected page
        logger.info("timeout not expired yet, secondsPassed = " + secondsPassed +
                    " lastHeartBeatTime=" + new Date(lastHB).toLocaleTimeString());
        arm();
      }
    }
    else {
      // heart beat does not exist, first time use (login happened in a different tab)
      logger.info("heart beat does not exist, first time use (login happened in a different tab)");
      arm();
      return;
    }
  }

  // enforceProtection
  //---------------------------------------------------------------------------
  function enforceProtection() {
    logger.debug("enforceProtection()");

    // depending on the mode:
    //
    // REGULAR: redirect the browser to login page only for this tab,
    //            sso session is still intact (until the user submits the password)
    // AGGRESSIVE: log out the user from CB SSO (all the cb sso apps are affected)
    //         - redirecting the browser to CB logout url (cleaner)
    //         or
    //         - deleting the sso cookies in JS and reloading the page

    switch (mode) {
      case MODES.REGULAR:
        // redirect to login
        redirectToLogin();

        break;

      case MODES.AGGRESSIVE:
        //logger.error(mode + " mode protection not implemented yet");

        // WARNING: it's very disruptive to do
        // not sure if it will ever be needed
        // consider forcing logout instead of (or in addition to) deleting cookies

        deleteCBCookies();
        window.location.assign (
          'https://account.collegeboard.org/login/login?DURL=' + window.location.href);

        break;
      default:
        logger.error("invalid mode protection:" + mode);
    }

  }

  // redirectToLogin
  //---------------------------------------------------------------------------
  function redirectToLogin() {
    window.location.assign (
        'https://account.collegeboard.org/login/login?DURL=' +
        encodeURIComponent(window.location.href));

    // stop the js execution on the page (so we won't flicker any sensitive data
    // even for a fleeting moment ;-) ) by throwing an uncaught exception
    throw 'stop js execution';
  }

  // deleteCBCookies
  //---------------------------------------------------------------------------
  function deleteCBCookies() {
    logger.warn('levity protector: session is invalid: deleting login cookie');
    cookie.remove(CB_COOKIE_NAMES.CB_LOGIN, {path: "/", domain: ".collegeboard.org"});
    cookie.remove(CB_COOKIE_NAMES.UL_SESSION, {path: "/", domain: ".collegeboard.org"});
  }

  // store page info in a browser local storage
  // this is needed to block a user from opening the timed out page
  // in a different tab
  // ----------------------------------------------------------------------------
  function storePageInfoInBrowserLS() {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(pageInfo));
  }

  // retrieve page info from a browser local storage
  // ----------------------------------------------------------------------------
  function loadPageInfoFromBrowserLS() {
    pageInfo = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)); // null is fine
    if (!pageInfo) {
      pageInfo = {forceLogin:false};
    }
  }

  // reset page info
  // ----------------------------------------------------------------------------
  function resetPageInfo() {
    if (!invisibleTimeoutEnforced)
      return;

    if (!pageInfo) {
      pageInfo = {};
    }

    pageInfo.forceLogin = false;
    storePageInfoInBrowserLS();
  }

  // isProtectionEnabledFunc
  //---------------------------------------------------------------------------
  function isProtectionEnabledFunc() {
    return isProtectionEnabled;
  }

  // execute init
  init();

  /**
   * cb core protector module public API
   */

  export default {

    /**
     * protection mode constants (to be used as in init's parama )
     */
    MODES : MODES,

    /**
     * init (mandatory)
     *
     * @param parameters object (optional)
     *    example: {
            tabRemovedTimeoutInSecs: 20, // optional, default: 10, make sure it's enough to reload the page
            mode: cb.core.protector.MODES.REGULAR} // optional, default: REGULAR
          }
     */
    init : init,

    /**
     * isProtectionEnabled
     */
    isProtectionEnabled : isProtectionEnabledFunc

  };
